import api, { STOREFRONT_LINKS } from '../Api'
import {ICollectionResponse} from "../../models/ICollectionResponse";
import {IStorefrontLink} from "../../models/Storefront";
import {IActionResponse} from "../../dal/BaseDAL";
import {cloneDeep} from "lodash";

const storefrontLinksApi = api.injectEndpoints({
    endpoints: (build) => ({
        // Both carry a tag purely so publish and revert can invalidate them. Ordinary edits still go
        // through the optimistic updateQueryData paths below, which are unaffected.
        getStorefrontHeaderLinks: build.query<ICollectionResponse<IStorefrontLink>, { storefrontId: string}>({
            query: (arg) => `storefront/storefronts/${arg.storefrontId}/links/header`,
            providesTags: (_result, _error, arg) => [{ type: STOREFRONT_LINKS, id: arg.storefrontId }]
        }),
        getStorefrontFooterLinks: build.query<ICollectionResponse<IStorefrontLink>, { storefrontId: string}>({
            query: (arg) => `storefront/storefronts/${arg.storefrontId}/links/footer`,
            providesTags: (_result, _error, arg) => [{ type: STOREFRONT_LINKS, id: arg.storefrontId }]
        }),
        saveStorefrontFooterLink: build.mutation<IActionResponse, {link: IStorefrontLink, storefront: string}>({
            query: (arg) => ({
                url:  `storefront/storefronts/${arg.storefront}/links?type=footer`,
                method: 'PUT',
                body: arg.link
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const result = await queryFulfilled;
                if (!result.data.success) {
                    return;
                }
                if(!arg.link.id){
                    arg.link.id = result.data.id
                }

                dispatch(
                    storefrontLinksApi.util.updateQueryData('getStorefrontFooterLinks', {storefrontId: arg.storefront}, draft => {
                        const copy = cloneDeep(draft)
                        const index = draft.items.findIndex(a => a.id && a.id === arg.link.id)
                        if (index !== -1) {
                            copy.items.splice(index, 1, arg.link)
                        } else {
                            copy.items.splice(copy.items.length, 0, arg.link)
                        }
                        copy.items.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                        Object.assign(draft, copy);

                    })
                );
            }
        }),
        saveStorefrontHeaderLink: build.mutation<IActionResponse, {link: IStorefrontLink, storefront: string}>({
            query: (arg) => ({
                url:  `storefront/storefronts/${arg.storefront}/links?type=header`,
                method: 'PUT',
                body: arg.link
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const result = await queryFulfilled;
                if (!result.data.success) {
                    return;
                }
                if(!arg.link.id){
                    arg.link.id = result.data.id
                }

                dispatch(
                    storefrontLinksApi.util.updateQueryData('getStorefrontHeaderLinks', {storefrontId: arg.storefront}, draft => {
                        const copy = cloneDeep(draft)
                        const index = draft.items.findIndex(a => a.id && a.id === arg.link.id)
                        if (index !== -1) {
                            copy.items.splice(index, 1, arg.link)
                        } else {
                            copy.items.splice(copy.items.length, 0, arg.link)
                        }
                        copy.items.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                        Object.assign(draft, copy);

                    })
                );
            }
        }),
        saveStorefrontHeaderLinks: build.mutation<IActionResponse, {links: IStorefrontLink[], storefront: string}>({
            query: (arg) => ({
                url:  `storefront/storefronts/${arg.storefront}/links/batch?type=header`,
                method: 'PUT',
                body: arg.links
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const patchResults = dispatch(
                    storefrontLinksApi.util.updateQueryData('getStorefrontHeaderLinks', {storefrontId: arg.storefront}, draft => {
                        const copy = cloneDeep(draft)
                        copy.items = [...arg.links]
                        copy.total = arg.links.length
                        Object.assign(draft, copy);
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResults.undo();
                }
            }
        }),
        saveStorefrontFooterLinks: build.mutation<IActionResponse, {links: IStorefrontLink[], storefront: string}>({
            query: (arg) => ({
                url:  `storefront/storefronts/${arg.storefront}/links/batch?type=footer`,
                method: 'PUT',
                body: arg.links
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const queryKey = {storefrontId: arg.storefront};
                const patchResults = dispatch(
                    storefrontLinksApi.util.updateQueryData('getStorefrontFooterLinks', queryKey, draft => {
                        const copy = cloneDeep(draft)
                        copy.items = [...arg.links]
                        copy.total = arg.links.length
                        Object.assign(draft, copy);
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResults.undo();
                }
            }
        }),
        
        deleteStorefrontFooterLink: build.mutation<IActionResponse, {storefront: string, id: string}>({
            query: (arg) => ({
                url: `storefront/storefronts/links/${arg.id}?type=footer`,
                method: 'DELETE',
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const patchResult = dispatch(
                    storefrontLinksApi.util.updateQueryData('getStorefrontFooterLinks', {storefrontId: arg.storefront}, draft => {
                        const copy = cloneDeep(draft)
                        const index = draft.items.findIndex(a => a.id === arg.id)
                        if(index !== -1){
                            copy.items.splice(index, 1)
                            Object.assign(draft, copy);
                        }

                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            }
        }),
        deleteStorefrontHeaderLink: build.mutation<IActionResponse, {storefront: string, id: string}>({
            query: (arg) => ({
                url: `storefront/storefronts/links/${arg.id}?type=header`,
                method: 'DELETE',
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const patchResult = dispatch(
                    storefrontLinksApi.util.updateQueryData('getStorefrontHeaderLinks', {storefrontId: arg.storefront}, draft => {
                        const copy = cloneDeep(draft)
                        const index = draft.items.findIndex(a => a.id === arg.id)
                        if(index !== -1){
                            copy.items.splice(index, 1)
                            Object.assign(draft, copy);
                        }

                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            }
        }),

    }),
    overrideExisting: false,
})

export const {
    useGetStorefrontHeaderLinksQuery,
    useGetStorefrontFooterLinksQuery,
    useSaveStorefrontFooterLinksMutation,
    useSaveStorefrontHeaderLinksMutation,
    useDeleteStorefrontFooterLinkMutation,
    useDeleteStorefrontHeaderLinkMutation,
    useSaveStorefrontFooterLinkMutation,
    useSaveStorefrontHeaderLinkMutation

} = storefrontLinksApi
